/**
 * Attendance Summary Component
 * Displays categorized sign-in/sign-out counts with Full Time subcategory breakdown
 * Includes touch-friendly tooltips showing children names for each count
 */
import { useState, useEffect, useRef } from "react";
import { Bus, X, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { StillInCenterByCategory } from "./StillInCenterByCategory";
import { AmBusPickupDialog } from "./AmBusPickupDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Touch AND hover friendly count component - works on desktop (hover) AND mobile (tap)
export const HoverCount = ({ label, count, children = [], colorClass = "text-green-600", boldClass = "font-bold" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  // Close tooltip when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);
  
  // Cleanup hover timeout
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  if (count === 0 || children.length === 0) {
    return (
      <div className="relative block">
        <p className={colorClass}>
          {label}: <span className={boldClass}>{count}</span>
        </p>
      </div>
    );
  }
  
  const handleMouseEnter = () => {
    // Delay before showing on hover (desktop)
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 200);
  };
  
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
  };
  
  const handleTap = (e) => {
    // Toggle on tap (mobile/tablet)
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const showTooltip = isOpen || isHovering;

  return (
    <div className="relative block" ref={containerRef}>
      <p 
        className={`${colorClass} cursor-pointer hover:underline active:underline select-none`}
        onClick={handleTap}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchEnd={handleTap}
      >
        {label}: <span className={boldClass}>{count}</span>
      </p>
      
      {/* Tooltip popup - shows on hover (desktop) or tap (mobile) */}
      {showTooltip && (
        <div 
          className="absolute z-[9999] left-0 top-full mt-2 bg-gray-900 text-white p-1.5 rounded shadow-xl min-w-[130px] max-w-[200px] whitespace-normal"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={handleMouseLeave}
        >
          {/* Close button - helpful for mobile */}
          <button 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsHovering(false); }}
            className="absolute top-1 right-1 p-1 hover:bg-gray-700 rounded"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="font-bold mb-1 text-[11px] border-b border-gray-600 pb-1 pr-4">{label} ({count})</p>
          <ul className="text-[11px] space-y-0.5 max-h-48 overflow-y-auto">
            {children.map((name, idx) => (
              <li key={idx} className="break-words">• {name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const AttendanceSummary = ({
  fullTimeSignIns,
  fullTimeCounts,
  fullTimeSubcategoryCounts = {},
  fullTimeSubcategoryChildren = {},
  partTimeSignIns,
  partTimeCounts,
  partTimeChildrenList = [],
  schoolAgeSignIns,
  schoolAgeCounts,
  schoolAgeChildrenLists = {},
  afterSchoolSignIns,
  afterSchoolCounts,
  afterSchoolChildrenList = [],
  uncategorizedSignIns,
  uncategorizedCounts,
  totalChildren,
  totalSignedIn,
  totalSignedOut,
  allSignedInChildren = [],
  allSignedOutChildren = [],
  signedOutSubcategoryCounts = {},
  signedOutSubcategoryChildren = {},
  stayAllDayCount = 0,
  stayAllDayNames = [],
  midDayCurrentlyOut = [],
  midDayReturnedToday = [],
  stillInData,
  date,
  currentUserName
}) => {
  const [busEntries, setBusEntries] = useState([]); // one per school recorded today
  const [amBusDialogOpen, setAmBusDialogOpen] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editTimeValue, setEditTimeValue] = useState("");
  const [editNameValue, setEditNameValue] = useState("");

  // Feature access check for bus tracking
  const { hasAccess: hasBusTrackingAccess } = useFeatureAccess('bus_tracking');
  
  // Fetch bus tracking data on mount and when date changes
  useEffect(() => {
    if (date) {
      fetchBusTracking();
    }
  }, [date]);
  
  const fetchBusTracking = async () => {
    try {
      const response = await axios.get(`${API}/bus-tracking/${date}`);
      const tracking = response.data.tracking || {};
      setBusEntries(tracking.am_pickup || []);
    } catch (error) {
      console.error("Error fetching bus tracking:", error);
    }
  };

  const handleClearAmBus = async (school) => {
    try {
      await axios.delete(`${API}/bus-tracking/${date}/am_pickup`, { params: { school } });
      toast.success(`A.M. Bus time cleared for ${school}`);
      fetchBusTracking();
    } catch (error) {
      console.error("Error clearing bus time:", error);
      toast.error("Failed to clear bus time");
    }
  };

  const startEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setEditTimeValue(entry.arrivalTime || "");
    setEditNameValue(entry.recordedBy || "");
  };

  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEditTimeValue("");
    setEditNameValue("");
  };

  const saveEditEntry = async (entry) => {
    try {
      await axios.patch(`${API}/bus-tracking/${date}/am_pickup`, {
        school: entry.school,
        arrivalTime: editTimeValue,
        recordedBy: editNameValue,
      });
      toast.success(`${entry.school}'s bus time updated`);
      cancelEditEntry();
      fetchBusTracking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update bus entry");
    }
  };

  if (totalChildren === 0) return null;

  return (
    <div 
      className="mt-6 rounded-xl p-4"
      style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(59,130,246,0.12)',
        border: '2px solid rgba(59,130,246,0.3)'
      }}
    >
      <h3 className="text-lg font-bold text-blue-900 mb-3">Daily Attendance Summary</h3>
      
      {/* 3 Tabs Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* Full Time Column — existing card + new signed-out breakdown card stacked */}
        <div className="flex flex-col gap-4">

          {/* === EXISTING FULL TIME CARD — UNCHANGED === */}
          <div
            className="rounded-xl p-4"
            style={{
              background: 'rgba(239,246,255,0.9)',
              border: '2px solid rgba(59,130,246,0.4)',
              boxShadow: '0 4px 12px rgba(59,130,246,0.15)'
            }}
          >
            <h4 className="text-lg font-bold text-blue-800 mb-2">FULL TIME</h4>
            <p className="text-blue-700 font-bold text-xl mb-2">
              Signed In Today: {fullTimeCounts.signedIn}
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs mb-2 border-t border-blue-200 pt-2">
              <HoverCount
                label="PreK"
                count={fullTimeSubcategoryCounts.prek || 0}
                children={fullTimeSubcategoryChildren.prek || []}
                colorClass="text-blue-600"
              />
              <HoverCount
                label="Older Toddler"
                count={fullTimeSubcategoryCounts.olderToddler || 0}
                children={fullTimeSubcategoryChildren.olderToddler || []}
                colorClass="text-blue-600"
              />
              <HoverCount
                label="Young Toddler"
                count={fullTimeSubcategoryCounts.youngToddler || 0}
                children={fullTimeSubcategoryChildren.youngToddler || []}
                colorClass="text-blue-600"
              />
              <HoverCount
                label="Infant"
                count={fullTimeSubcategoryCounts.infant || 0}
                children={fullTimeSubcategoryChildren.infant || []}
                colorClass="text-blue-600"
              />
            </div>
          </div>

          {/* === NEW CARD: Full Time Signed Out by Subcategory === */}
          <div
            className="rounded-xl p-4"
            style={{
              background: 'rgba(236,253,245,0.9)',
              border: '2px solid rgba(16,185,129,0.4)',
              boxShadow: '0 4px 12px rgba(16,185,129,0.12)'
            }}
          >
            <h4 className="text-sm font-bold text-emerald-800 mb-2 uppercase tracking-wide">
              Full Time — Signed Out by Category
            </h4>
            <p className="text-emerald-700 font-bold text-xl mb-2">
              Signed Out Today: {fullTimeCounts.signedOut}
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs border-t border-emerald-200 pt-2">
              <HoverCount
                label="PreK"
                count={signedOutSubcategoryCounts.prek || 0}
                children={signedOutSubcategoryChildren.prek || []}
                colorClass="text-emerald-700"
              />
              <HoverCount
                label="Older Toddler"
                count={signedOutSubcategoryCounts.olderToddler || 0}
                children={signedOutSubcategoryChildren.olderToddler || []}
                colorClass="text-emerald-700"
              />
              <HoverCount
                label="Young Toddler"
                count={signedOutSubcategoryCounts.youngToddler || 0}
                children={signedOutSubcategoryChildren.youngToddler || []}
                colorClass="text-emerald-700"
              />
              <HoverCount
                label="Infant"
                count={signedOutSubcategoryCounts.infant || 0}
                children={signedOutSubcategoryChildren.infant || []}
                colorClass="text-emerald-700"
              />
            </div>
          </div>

        </div>

        {/* Tab 2: School Age */}
        <div 
          className="rounded-xl p-4"
          style={{
            background: 'rgba(240,253,244,0.9)',
            border: '2px solid rgba(34,197,94,0.4)',
            boxShadow: '0 4px 12px rgba(34,197,94,0.15)'
          }}
        >
          <h4 className="text-lg font-bold text-green-800 mb-2">SCHOOL AGE</h4>
          <div className="space-y-1">
            <HoverCount 
              label="AM Signed In Today" 
              count={schoolAgeCounts.amSignedIn} 
              children={schoolAgeChildrenLists.amSignedIn || []}
              colorClass="text-purple-600 font-medium"
              boldClass="text-purple-700 font-bold text-lg"
            />
            <HoverCount 
              label="PM Drop Off Today" 
              count={schoolAgeCounts.pmDropOff} 
              children={schoolAgeChildrenLists.pmDropOff || []}
              colorClass="text-green-600 font-medium"
              boldClass="text-green-700 font-bold text-lg"
            />
            <HoverCount 
              label="PM Out" 
              count={schoolAgeCounts.pmPickedUp} 
              children={schoolAgeChildrenLists.pmOut || []}
              colorClass="text-blue-600 font-medium"
              boldClass="text-blue-700 font-bold text-lg"
            />
            {stayAllDayCount > 0 && (
              <HoverCount
                label="Staying All Day"
                count={stayAllDayCount}
                children={stayAllDayNames}
                colorClass="text-emerald-700 font-semibold text-sm"
                boldClass="text-emerald-800 font-bold"
              />
            )}
          </div>
          
          {/* A.M. Bus Picked Up Feature - Premium+ only */}
          {hasBusTrackingAccess && (
          <div className="mt-3 pt-3 border-t border-green-200 space-y-2">
            {busEntries.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between bg-green-100 rounded-lg px-3 py-2 border border-green-300" data-testid={`am-bus-entry-${entry.id}`}>
                {editingEntryId === entry.id ? (
                  <>
                    <div className="flex items-center gap-2 flex-1">
                      <Bus className="w-5 h-5 text-green-700 shrink-0" />
                      <span className="text-green-800 font-semibold text-sm shrink-0">{entry.school}:</span>
                      <input
                        type="text"
                        value={editTimeValue}
                        onChange={(e) => setEditTimeValue(e.target.value)}
                        placeholder="e.g., 9:13 AM"
                        className="border border-green-400 rounded px-2 py-1 text-sm w-24 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid={`am-bus-edit-time-${entry.id}`}
                        autoFocus
                      />
                      <span className="text-xs text-green-700 shrink-0">by</span>
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        placeholder="Staff name"
                        className="border border-green-400 rounded px-2 py-1 text-sm flex-1 min-w-0 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid={`am-bus-edit-name-${entry.id}`}
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button onClick={() => saveEditEntry(entry)} className="p-1 rounded hover:bg-green-200 transition-colors" title="Save" data-testid={`am-bus-save-${entry.id}`}>
                        <Check className="w-4 h-4 text-green-700" />
                      </button>
                      <button onClick={cancelEditEntry} className="p-1 rounded hover:bg-green-200 transition-colors" title="Cancel" data-testid={`am-bus-cancel-edit-${entry.id}`}>
                        <X className="w-4 h-4 text-green-700" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Bus className="w-5 h-5 text-green-700" />
                      <div>
                        <p className="text-green-800 font-semibold text-sm">
                          {entry.school}: <span className="text-green-900">{entry.arrivalTime}</span>
                        </p>
                        {entry.recordedBy && (
                          <p className="text-xs text-green-600">by {entry.recordedBy}{entry.childIdsMarked?.length ? ` — ${entry.childIdsMarked.length} marked departed` : ''}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditEntry(entry)}
                        className="p-1 rounded hover:bg-green-200 transition-colors"
                        title="Edit time/name"
                        data-testid={`am-bus-edit-${entry.id}`}
                      >
                        <Pencil className="w-4 h-4 text-green-700" />
                      </button>
                      <button
                        onClick={() => handleClearAmBus(entry.school)}
                        className="p-1 rounded hover:bg-green-200 transition-colors"
                        title="Clear bus time"
                        data-testid={`am-bus-clear-${entry.id}`}
                      >
                        <X className="w-4 h-4 text-green-700" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            <Button
              onClick={() => setAmBusDialogOpen(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2"
              data-testid="am-bus-pickup-btn"
            >
              <Bus className="w-5 h-5" />
              {busEntries.length > 0 ? "Record Another School's AM Bus" : "A.M. Bus Picked Up"}
            </Button>
          </div>
          )}
        </div>

        <AmBusPickupDialog
          open={amBusDialogOpen}
          onOpenChange={setAmBusDialogOpen}
          date={date}
          currentUserName={currentUserName}
          onRecorded={fetchBusTracking}
        />

        {/* Tab 3: Part Time & After School */}
        <div 
          className="rounded-xl p-4"
          style={{
            background: 'rgba(250,245,255,0.8)',
            border: '2px solid rgba(147,51,234,0.4)',
            boxShadow: '0 4px 12px rgba(147,51,234,0.15)'
          }}
        >
          <h4 className="text-lg font-bold text-purple-800 mb-2">PART TIME & AFTER SCHOOL</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HoverCount 
                label="Part Time" 
                count={partTimeSignIns.length} 
                children={partTimeChildrenList}
                colorClass="text-purple-600 font-medium"
                boldClass="text-purple-700 font-bold text-xl"
              />
              <span className="text-sm text-gray-500">(In: {partTimeCounts.signedIn} | Out: {partTimeCounts.signedOut})</span>
            </div>
            <div className="flex items-center gap-2">
              <HoverCount 
                label="After School" 
                count={afterSchoolSignIns.length} 
                children={afterSchoolChildrenList}
                colorClass="text-orange-600 font-medium"
                boldClass="text-orange-700 font-bold text-xl"
              />
              <span className="text-sm text-gray-500">(In: {afterSchoolCounts.signedIn} | Out: {afterSchoolCounts.signedOut})</span>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Who Is Still In The Center By Category — ratio compliance view */}
      {stillInData && (
        <StillInCenterByCategory {...stillInData} />
      )}

      {/* Total Summary Row */}
      <div 
        className="mt-4 rounded-2xl p-4 flex justify-between items-center relative"
        style={{
          background: 'linear-gradient(135deg, rgba(219,234,254,0.95) 0%, rgba(191,219,254,0.9) 50%, rgba(224,231,255,0.95) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.6)',
          border: '1px solid rgba(59,130,246,0.3)',
          position: 'relative',
          zIndex: 20
        }}
      >
        {/* Decorative highlight bar at top */}
        <div 
          className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
          style={{
            background: 'linear-gradient(90deg, rgba(59,130,246,0.6) 0%, rgba(99,102,241,0.6) 50%, rgba(59,130,246,0.6) 100%)'
          }}
        />
        <span className="text-blue-900 font-bold text-lg">TOTAL CHILDREN: {totalChildren}</span>
        <div className="flex gap-4 flex-wrap items-baseline">
          <HoverCount 
            label="Signed In" 
            count={totalSignedIn} 
            children={allSignedInChildren}
            colorClass="text-green-700 font-semibold"
            boldClass="font-bold"
          />
          <HoverCount 
            label="Signed Out" 
            count={totalSignedOut} 
            children={allSignedOutChildren}
            colorClass="text-blue-700 font-semibold"
            boldClass="font-bold"
          />
          {midDayCurrentlyOut.length > 0 && (
            <HoverCount
              label="Mid-Day Out"
              count={midDayCurrentlyOut.length}
              children={midDayCurrentlyOut.map(s => s.childName || 'Unknown')}
              colorClass="text-amber-700 font-semibold"
              boldClass="font-bold"
            />
          )}
          {midDayReturnedToday.length > 0 && (
            <HoverCount
              label="Mid-Day Returned"
              count={midDayReturnedToday.length}
              children={midDayReturnedToday.map(s => s.childName || 'Unknown')}
              colorClass="text-emerald-700 font-semibold"
              boldClass="font-bold"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceSummary;
